/**
 * Timezone helpers for the doctor app.
 *
 * Why this file exists: a doctor's working hours are stored as bare wall-clock times
 * ("08:00"-"22:00") with no zone attached, which is unambiguous only while every doctor and every
 * patient shares one offset. ONLINE consultations break that assumption, so the backend needs to
 * know which zone the doctor's hours are expressed in before it can show them to a patient
 * elsewhere in the world. The browser already knows the answer; this reports it.
 *
 * It also replaces the hardcoded "Asia/Baghdad" / "Asia/Kolkata" literals scattered through the
 * app, which were wrong for any doctor not sitting in that exact zone.
 */

/**
 * The platform timezone (Iraq). Fixed for the PHARMACY dashboard: every date a pharmacy user sees is
 * in this zone, whatever the device's clock says, and the pharmacy app never reports a zone. Same
 * value as `PLATFORM_TIMEZONE` in tiryaq-healthcare-shared; the server stores it for pharmacy
 * accounts. Doctors are not affected: their zone follows their device (see TimezoneNotice).
 */
export const PLATFORM_TIMEZONE = "Asia/Baghdad";

/** role_id of a pharmacy account (doctors are 4). Stored in sessionStorage `usr_role_id` at login. */
const PHARMACY_ROLE_ID = "5";

/** Is the signed-in user on the fixed platform zone? True for pharmacy accounts. */
export const hasFixedTimezone = () => {
  try {
    return sessionStorage.getItem("usr_role_id") === PHARMACY_ROLE_ID;
  } catch {
    return false;
  }
};

/** The zone to assume when the browser will not tell us — the platform default. */
export const DEFAULT_TIMEZONE = PLATFORM_TIMEZONE;

/**
 * The browser's IANA timezone, e.g. "Asia/Baghdad" or "Asia/Kolkata".
 *
 * Returns DEFAULT_TIMEZONE when the runtime reports something the backend will refuse, so callers
 * never have to handle a missing value. Two shapes are rejected on purpose, because both are
 * accepted by `Intl` yet unusable for scheduling:
 *   - a fixed UTC offset ("+05:30") carries no daylight-saving rules, so a converted appointment
 *     silently drifts by an hour for part of the year;
 *   - a bare abbreviation ("IST") is ambiguous — India, Israel and Irish Standard Time.
 * The api-gateway applies the same rule, so anything this returns is guaranteed to be accepted.
 */
export const detectTimezone = () => {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected) return DEFAULT_TIMEZONE;
    if (/^[+-]/.test(detected)) return DEFAULT_TIMEZONE;

    const upper = detected.toUpperCase();
    if (!detected.includes("/") && upper !== "UTC" && upper !== "GMT") {
      return DEFAULT_TIMEZONE;
    }

    return detected;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};
